import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAccount, useChainId } from 'wagmi';
import { formatEther, formatUnits } from 'viem';
import {
  ArrowLeft,
  Clock,
  Copy,
  Share2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Wallet,
  Loader2,
  CircleDollarSign,
  Trophy,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { GameIcon } from '@/components/ui/GameIcon';
import { getGameName } from '@/lib/gameConfig';
import { api, type Wager } from '@/lib/api';
import { formatCurrency, formatRelativeTime, cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { useWallet } from '@/hooks/useWallet';
import {
  useFundEscrow,
  useCollectWinnings,
  useUSDCBalance,
  useHasPlayerFunded,
  FUNDING_STEP_LABELS,
} from '@/hooks/useEscrow';
import { ESCROW_CHAIN_ID } from '@/lib/contracts';
import { useSocket, useWagerSubscription } from '@/hooks/useSocket';

const statusConfig: Record<
  string,
  { label: string; color: string; icon: React.ElementType }
> = {
  open: { label: 'Open', color: 'bg-blue-500', icon: Clock },
  agreed: { label: 'Agreed', color: 'bg-yellow-500', icon: AlertCircle },
  funding: { label: 'Funding', color: 'bg-orange-500', icon: Wallet },
  locked: { label: 'Locked', color: 'bg-purple-500', icon: CheckCircle },
  in_progress: { label: 'In Progress', color: 'bg-primary', icon: Clock },
  pending_verification: { label: 'Verifying', color: 'bg-yellow-500', icon: Clock },
  ready_to_collect: { label: 'Ready to Collect', color: 'bg-green-500', icon: CheckCircle },
  settled: { label: 'Settled', color: 'bg-green-500', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-muted', icon: XCircle },
  voided: { label: 'Voided', color: 'bg-destructive', icon: XCircle },
};

// ============================================
// Funding Section Component
// ============================================

function FundingSection({ wager, wagerId }: { wager: Wager; wagerId: string }) {
  const queryClient = useQueryClient();
  const { address, isConnected } = useAccount();
  const { ethBalance, isLoadingBalance, fundWallet: addFunds, switchChain } = useWallet();
  const { balance: usdcBalance, isLoadingBalance: isLoadingUsdc } = useUSDCBalance();
  const currentUser = useAuthStore((s) => s.user);
  const [escrowWaitStart] = useState(() => Date.now());

  const isCreator = wager.creator?.id === currentUser?.id;
  const escrowWagerId = wager.escrowWagerId as `0x${string}` | undefined;

  const { hasFunded: myFundedOnChain } = useHasPlayerFunded(escrowWagerId, address);

  const alreadyFunded = isCreator ? wager.creatorFunded : wager.opponentFunded;
  const isFundedOnChain = myFundedOnChain === true;

  const fundMutation = useMutation({
    mutationFn: (txHash: string) => api.fundWager(wagerId, txHash),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wager', wagerId] });
    },
  });

  const {
    fund,
    reset: resetFund,
    step,
    errorMsg,
    requiredDeposit,
    isLoadingDeposit,
    isProcessing,
    needsChainSwitch,
  } = useFundEscrow({
    wager,
    onSuccess: (txHash) => {
      fundMutation.mutate(txHash);
    },
    onError: () => {
      // error state handled by step/errorMsg
    },
  });

  const isETH = wager.currency === 'ETH';
  const stake = parseFloat(wager.amount);
  const feePercent = parseFloat(wager.platformFeePercent ?? '1');
  const feePerPlayer = stake * (feePercent / 100);
  const escrowPerPlayer = stake - feePerPlayer;
  // Player deposits exact stake amount. 1% fee is taken from the deposit.
  const totalRequired = stake;

  // Balance checks
  const currentBalance = isETH
    ? parseFloat(ethBalance)
    : parseFloat(usdcBalance ?? '0');
  const balanceLoading = isETH ? isLoadingBalance : isLoadingUsdc;
  const hasSufficientBalance = currentBalance >= totalRequired;
  const currencyLabel = isETH ? 'ETH' : 'USDC';

  // Format required deposit from contract (bigint)
  const formattedDeposit = requiredDeposit
    ? isETH
      ? formatEther(requiredDeposit)
      : formatUnits(requiredDeposit, 6)
    : null;

  // Funding deadline countdown
  const deadlineText = wager.fundingDeadline
    ? formatTimeRemaining(wager.fundingDeadline)
    : null;

  // If escrow not initialized yet (agreed but no escrowWagerId)
  if (wager.status === 'agreed' && !wager.escrowWagerId) {
    const waitingTooLong = Date.now() - escrowWaitStart > 30_000;
    return (
      <Card className={cn(
        'border-yellow-500/50',
        waitingTooLong ? 'bg-destructive/5 border-destructive/50' : 'bg-yellow-500/5'
      )}>
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            {waitingTooLong ? (
              <AlertCircle className="h-5 w-5 text-destructive" />
            ) : (
              <Loader2 className="h-5 w-5 animate-spin text-yellow-500" />
            )}
            <div>
              <p className="font-medium">
                {waitingTooLong ? 'Escrow Setup Delayed' : 'Initializing Escrow'}
              </p>
              <p className="text-sm text-muted-foreground">
                {waitingTooLong
                  ? 'The escrow setup is taking longer than expected. This will be resolved shortly — no action needed.'
                  : 'The on-chain escrow is being set up. This usually takes a few seconds.'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CircleDollarSign className="h-5 w-5" />
          Fund Escrow
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Deposit Breakdown */}
        <div className="rounded-lg bg-muted/50 p-4 space-y-2">
          <div className="flex justify-between text-sm font-medium">
            <span>You deposit</span>
            <span>
              {formattedDeposit
                ? `${parseFloat(formattedDeposit).toFixed(isETH ? 6 : 2)} ${currencyLabel}`
                : formatCurrency(stake, currencyLabel)}
            </span>
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Platform fee ({feePercent}%)</span>
            <span>-{formatCurrency(feePerPlayer, currencyLabel)}</span>
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Your escrow</span>
            <span>{formatCurrency(escrowPerPlayer, currencyLabel)}</span>
          </div>
          <div className="border-t border-border pt-2 flex justify-between text-sm font-medium">
            <span>Winner receives</span>
            <span className="text-green-500">{formatCurrency(escrowPerPlayer * 2, currencyLabel)}</span>
          </div>
        </div>

        {/* Wallet Balance */}
        {isConnected && (
          <div className="flex justify-between items-center text-sm">
            <span className="text-muted-foreground">Your {currencyLabel} balance</span>
            {balanceLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <span className={cn('font-medium', hasSufficientBalance ? 'text-green-500' : 'text-red-500')}>
                {currentBalance.toFixed(isETH ? 6 : 2)} {currencyLabel}
              </span>
            )}
          </div>
        )}

        {/* Funding Status */}
        <div className="flex gap-3">
          <FundingBadge
            label="Creator"
            funded={!!wager.creatorFunded}
          />
          <FundingBadge
            label="Opponent"
            funded={!!wager.opponentFunded}
          />
        </div>

        {/* Funding Deadline */}
        {deadlineText && (
          <p className="text-xs text-muted-foreground text-center">
            Funding deadline: {deadlineText}
          </p>
        )}

        {/* Action Button */}
        {alreadyFunded || isFundedOnChain ? (
          <div className="flex items-center justify-center gap-2 rounded-lg bg-green-500/10 p-3">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <span className="font-medium text-green-500">You have funded this challenge</span>
          </div>
        ) : !isConnected ? (
          <Button variant="gradient" size="lg" className="w-full" disabled>
            Connect wallet to fund
          </Button>
        ) : needsChainSwitch ? (
          <Button
            variant="gradient"
            size="lg"
            className="w-full gap-2"
            onClick={() => switchChain(ESCROW_CHAIN_ID)}
          >
            Switch Network
          </Button>
        ) : !hasSufficientBalance && !balanceLoading ? (
          <Button
            variant="gradient"
            size="lg"
            className="w-full gap-2"
            onClick={addFunds}
          >
            <Wallet className="h-4 w-4" />
            Add Funds
          </Button>
        ) : (
          <Button
            variant="gradient"
            size="lg"
            className="w-full gap-2"
            onClick={fund}
            disabled={isProcessing || isLoadingDeposit || fundMutation.isPending}
          >
            {isProcessing || fundMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {FUNDING_STEP_LABELS[step] || 'Processing...'}
              </>
            ) : (
              <>
                <Wallet className="h-4 w-4" />
                Fund Escrow
              </>
            )}
          </Button>
        )}

        {/* Error Display */}
        {step === 'error' && errorMsg && (
          <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
            <p className="font-medium">Transaction failed</p>
            <p className="mt-1 text-xs break-all">{errorMsg}</p>
            <Button variant="ghost" size="sm" className="mt-2" onClick={resetFund}>
              Try again
            </Button>
          </div>
        )}

        {fundMutation.error && (
          <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
            <p>Failed to record funding on server: {fundMutation.error.message}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function FundingBadge({ label, funded }: { label: string; funded: boolean }) {
  return (
    <div className={cn(
      'flex-1 flex items-center justify-center gap-2 rounded-lg p-2 text-sm',
      funded ? 'bg-green-500/10 text-green-500' : 'bg-muted/50 text-muted-foreground'
    )}>
      {funded ? <CheckCircle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
      <span>{label}: {funded ? 'Funded' : 'Pending'}</span>
    </div>
  );
}

// ============================================
// Collect Section Component
// ============================================

function CollectSection({ wager, wagerId, winnerPayout }: { wager: Wager; wagerId: string; winnerPayout: number }) {
  const queryClient = useQueryClient();
  const { isConnected } = useAccount();
  const { switchChain } = useWallet();
  const chainId = useChainId();

  const collectMutation = useMutation({
    mutationFn: (txHash: string) => api.recordCollection(wagerId, txHash),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wager', wagerId] });
    },
  });

  const {
    collect,
    reset: resetCollect,
    step,
    stepLabel,
    errorMsg,
    isProcessing,
  } = useCollectWinnings({
    wager,
    onSuccess: (txHash) => {
      collectMutation.mutate(txHash);
    },
  });

  const needsChainSwitch = chainId !== ESCROW_CHAIN_ID;

  return (
    <div className="space-y-3">
      {!isConnected ? (
        <Button variant="gradient" size="lg" className="w-full" disabled>
          Connect wallet to collect
        </Button>
      ) : needsChainSwitch ? (
        <Button
          variant="gradient"
          size="lg"
          className="w-full gap-2"
          onClick={() => switchChain(ESCROW_CHAIN_ID)}
        >
          Switch Network
        </Button>
      ) : (
        <Button
          variant="gradient"
          size="lg"
          className="w-full gap-2"
          onClick={collect}
          disabled={isProcessing || collectMutation.isPending}
        >
          {isProcessing || collectMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {stepLabel || 'Processing...'}
            </>
          ) : (
            <>
              <Trophy className="h-4 w-4" />
              Collect Winnings ({formatCurrency(winnerPayout)})
            </>
          )}
        </Button>
      )}

      {step === 'error' && errorMsg && (
        <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
          <p className="font-medium">Transaction failed</p>
          <p className="mt-1 text-xs break-all">{errorMsg}</p>
          <Button variant="ghost" size="sm" className="mt-2" onClick={resetCollect}>
            Try again
          </Button>
        </div>
      )}

      {collectMutation.error && (
        <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
          <p>Failed to record collection: {collectMutation.error.message}</p>
        </div>
      )}
    </div>
  );
}

function formatTimeRemaining(deadline: string): string {
  const diff = new Date(deadline).getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const mins = Math.floor(diff / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  if (mins > 60) {
    const hours = Math.floor(mins / 60);
    return `${hours}h ${mins % 60}m remaining`;
  }
  return `${mins}m ${secs}s remaining`;
}

// ============================================
// Main Page Component
// ============================================

export function WagerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);

  const { data: wager, isLoading } = useQuery({
    queryKey: ['wager', id],
    queryFn: () => api.getWager(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const w = query.state.data;
      if (!w) return false;
      // Poll aggressively when waiting for escrow initialization
      if (w.status === 'agreed' && !w.escrowWagerId) return 2000;
      // Poll frequently during active states
      if (['agreed', 'funding'].includes(w.status)) return 5000;
      if (['locked', 'in_progress'].includes(w.status)) return 10000;
      if (['pending_verification'].includes(w.status)) return 5000;
      return false;
    },
  });

  // Subscribe to real-time wager updates via WebSocket
  const { on, off } = useSocket();
  useWagerSubscription(id ?? null);

  const handleWagerUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['wager', id] });
  }, [queryClient, id]);

  useEffect(() => {
    on('wager:matched', handleWagerUpdate);
    on('wager:updated', handleWagerUpdate);
    on('wager:settled', handleWagerUpdate);

    return () => {
      off('wager:matched', handleWagerUpdate);
      off('wager:updated', handleWagerUpdate);
      off('wager:settled', handleWagerUpdate);
    };
  }, [on, off, handleWagerUpdate]);

  const acceptMutation = useMutation({
    mutationFn: () => api.acceptWager(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wager', id] });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.cancelWager(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wager', id] });
    },
  });

  const isCreator = wager?.creator?.id === currentUser?.id;
  const canCancel = isCreator && wager && ['open', 'agreed', 'funding'].includes(wager.status);

  // Fee calculations for display
  const stakeAmount = wager ? parseFloat(wager.amount) : 0;
  const feeRate = wager ? parseFloat(wager.platformFeePercent ?? '1') / 100 : 0.01;
  const escrowPerPlayer = stakeAmount * (1 - feeRate);
  const winnerPayout = escrowPerPlayer * 2;

  if (isLoading) {
    return (
      <div className="flex h-60 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!wager) {
    return (
      <div className="text-center">
        <h1 className="font-heading text-2xl font-bold">Challenge not found</h1>
        <Link to="/wagers">
          <Button variant="link" className="mt-4">
            Back to challenges
          </Button>
        </Link>
      </div>
    );
  }

  const status = statusConfig[wager.status] || statusConfig.open;
  const StatusIcon = status.icon;

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
  };

  const showFundingSection =
    wager.status === 'agreed' || wager.status === 'funding';

  return (
    <div className="mx-auto max-w-2xl space-y-6 pb-20 lg:pb-0">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/wagers">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-bold">Challenge Details</h1>
          <p className="text-sm text-muted-foreground">
            Created {formatRelativeTime(wager.createdAt)}
          </p>
        </div>
        <Button variant="outline" size="icon" onClick={handleShare}>
          <Share2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Status */}
      <Card>
        <CardContent className="flex items-center gap-4 p-6">
          <div className={cn('rounded-full p-3', status.color)}>
            <StatusIcon className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1">
            <p className="font-heading text-xl font-bold">{status.label}</p>
            <p className="text-sm text-muted-foreground">
              {wager.status === 'open' && 'Waiting for an opponent to accept'}
              {wager.status === 'agreed' && 'Escrow initializing — both players need to fund'}
              {wager.status === 'funding' && 'Both players need to fund the escrow'}
              {wager.status === 'locked' && 'Funds locked — play your match now!'}
              {wager.status === 'in_progress' && 'Match in progress — good luck!'}
              {wager.status === 'pending_verification' && 'Match found — verifying result...'}
              {wager.status === 'ready_to_collect' && 'Winner declared — collect your winnings!'}
              {wager.status === 'settled' && 'Match complete, winnings distributed'}
              {wager.status === 'voided' && 'Match voided — both players refunded'}
              {wager.status === 'cancelled' && 'Challenge was cancelled'}
              {wager.status === 'disputed' && 'This match is under dispute'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Challenge Code */}
      {wager.visibility === 'private' && wager.challengeCode && (
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="flex items-center justify-between p-4">
            <div>
              <p className="text-sm text-muted-foreground">Challenge Link</p>
              <p className="font-mono text-lg font-bold">
                pullup.gg/c/{wager.challengeCode}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigator.clipboard.writeText(`https://pullup.gg/c/${wager.challengeCode}`)
              }
            >
              <Copy className="mr-2 h-4 w-4" />
              Copy
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Players */}
      <Card>
        <CardHeader>
          <CardTitle>Players</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Avatar className="h-12 w-12">
              <AvatarImage src={wager.creator.avatarUrl} />
              <AvatarFallback>{wager.creator.displayName[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="font-medium">{wager.creator.displayName}</p>
              <p className="text-sm text-muted-foreground">Creator</p>
            </div>
            {wager.creatorFunded ? (
              <Badge className="bg-green-500/20 text-green-500">Funded</Badge>
            ) : (
              <Badge variant="outline">Ready</Badge>
            )}
          </div>

          {wager.opponent ? (
            <div className="flex items-center gap-4">
              <Avatar className="h-12 w-12">
                <AvatarImage src={wager.opponent.avatarUrl} />
                <AvatarFallback>{wager.opponent.displayName[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="font-medium">{wager.opponent.displayName}</p>
                <p className="text-sm text-muted-foreground">Opponent</p>
              </div>
              {wager.opponentFunded ? (
                <Badge className="bg-green-500/20 text-green-500">Funded</Badge>
              ) : (
                <Badge variant="outline">Ready</Badge>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-4 rounded-xl border border-dashed p-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <span className="text-2xl">?</span>
              </div>
              <div className="flex-1">
                <p className="font-medium text-muted-foreground">Waiting for opponent</p>
                <p className="text-sm text-muted-foreground">Share the link to challenge someone</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Details */}
      <Card>
        <CardHeader>
          <CardTitle>Challenge Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Game</span>
              <div className="flex items-center gap-2">
                <GameIcon gameCode={wager.gameCode} size="sm" />
                <span className="font-medium">{getGameName(wager.gameCode)}</span>
              </div>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Type</span>
              <span className="font-medium">{wager.wagerType}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Stake (per player)</span>
              <span className="font-medium">{formatCurrency(wager.amount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Pot</span>
              <span className="font-heading text-lg font-bold text-primary">
                {formatCurrency(winnerPayout)}
              </span>
            </div>
            {wager.expiresAt && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Expires</span>
                <span className="font-medium">{formatRelativeTime(wager.expiresAt)}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Funding Section */}
      {showFundingSection && <FundingSection wager={wager} wagerId={id!} />}

      {/* Match Detection Status - Searching */}
      {wager.status === 'locked' && (
        <Card className="border-purple-500/50 bg-purple-500/5">
          <CardContent className="p-6">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="relative">
                <div className="h-16 w-16 animate-spin rounded-full border-4 border-purple-500/30 border-t-purple-500" />
                <GameIcon
                  gameCode={wager.gameCode}
                  size="sm"
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-transparent"
                />
              </div>
              <div>
                <p className="font-heading text-lg font-bold">Searching for Match...</p>
                <p className="text-sm text-muted-foreground">
                  Play your {getGameName(wager.gameCode)} match now. The system will automatically detect it.
                </p>
              </div>
              {wager.matchDeadline && (
                <p className="text-xs text-muted-foreground">
                  Match deadline: {formatTimeRemaining(wager.matchDeadline)}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Match Detection Status - Match Found */}
      {wager.status === 'in_progress' && (
        <Card className="border-green-500/50 bg-green-500/5">
          <CardContent className="p-6">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="relative">
                <div className="h-16 w-16 rounded-full border-4 border-green-500 flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </div>
              <div>
                <p className="font-heading text-lg font-bold text-green-500">Match Found, GLHF!</p>
                <p className="text-sm text-muted-foreground">
                  Your {getGameName(wager.gameCode)} match is in progress. The result will be detected automatically.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Match Result */}
      {wager.status === 'pending_verification' && (
        <Card className="border-yellow-500/50 bg-yellow-500/5">
          <CardContent className="p-6">
            <div className="flex flex-col items-center gap-3 text-center">
              <Loader2 className="h-10 w-10 animate-spin text-yellow-500" />
              <div>
                <p className="font-heading text-lg font-bold">Match Detected!</p>
                <p className="text-sm text-muted-foreground">
                  Verifying result and settling on-chain...
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Winner Announcement */}
      {wager.status === 'ready_to_collect' && wager.winner && (
        <Card className="border-green-500/50 bg-green-500/5">
          <CardContent className="p-6">
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="rounded-full bg-green-500/20 p-4">
                <Trophy className="h-8 w-8 text-green-500" />
              </div>
              <div>
                <p className="font-heading text-xl font-bold text-green-500">
                  {wager.winner.displayName} Wins!
                </p>
                <p className="text-sm text-muted-foreground">
                  Pot: {formatCurrency(winnerPayout)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Draw / Voided */}
      {wager.status === 'voided' && (
        <Card className="border-muted">
          <CardContent className="p-6">
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="rounded-full bg-muted p-4">
                <AlertCircle className="h-8 w-8 text-muted-foreground" />
              </div>
              <div>
                <p className="font-heading text-lg font-bold">Draw — Both Players Refunded</p>
                <p className="text-sm text-muted-foreground">
                  The match ended in a draw. Funds have been returned to both players.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Settled */}
      {wager.status === 'settled' && wager.winner && (
        <Card className="border-green-500/50 bg-green-500/5">
          <CardContent className="p-6">
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="rounded-full bg-green-500/20 p-4">
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <div>
                <p className="font-heading text-xl font-bold text-green-500">
                  {wager.winner.displayName} Won!
                </p>
                <p className="text-sm text-muted-foreground">
                  Winnings have been distributed.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      {wager.status === 'open' && !wager.opponent && currentUser?.id !== wager.creator?.id && (
        <Button
          variant="gradient"
          size="lg"
          className="w-full"
          onClick={() => acceptMutation.mutate()}
          disabled={acceptMutation.isPending}
        >
          {acceptMutation.isPending ? 'Accepting...' : 'Accept Challenge'}
        </Button>
      )}

      {wager.status === 'ready_to_collect' && wager.winner && (
        currentUser?.id === wager.winner.id ? (
          <CollectSection wager={wager} wagerId={id!} winnerPayout={winnerPayout} />
        ) : (
          <div className="flex items-center justify-center gap-2 rounded-lg bg-muted/50 p-4">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              Waiting for {wager.winner.displayName} to collect winnings
            </span>
          </div>
        )
      )}

      {canCancel && (
        <Button
          variant="destructive"
          size="lg"
          className="w-full gap-2"
          onClick={() => cancelMutation.mutate()}
          disabled={cancelMutation.isPending}
        >
          <XCircle className="h-4 w-4" />
          {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Challenge'}
        </Button>
      )}
    </div>
  );
}
